/**
 * SiteHome — the finding-tool home view for the site root (`Plone Site`).
 *
 * Volto 19 port of the Claude Design "home" screen
 * (design/ui_kits/home/HomeScreen.jsx — hero search band + a two-column
 * "Recently added" / "Recently updated" results grid) onto the real addon:
 * real per-site data (searchContent, see ./queries.js + ./asyncData.js),
 * real routes, and the design system's `.prc-*` DOM (ds-core.css, globally
 * loaded — see PORT-SPEC.md CONTRACT A "status pill" / CONTRACT B "results
 * table") instead of the design kit's mock ResultsTable. User direction
 * 2026-07-15 ("home is a finding tool, not a brochure"): search-first,
 * dense, no decorative icons, real recent documents + compact counts.
 *
 * SSR: the two listing queries and three count queries are dispatched
 * server-side by ./asyncData.js's `homeAsyncPropExtender` (registered by
 * index.ts — see the build report for the exact wiring); the effect below
 * is a client-only safety net (skips any subrequest already loaded/loading)
 * so a client-side SPA navigation into "/" still gets fresh data even if
 * the extender somehow doesn't re-fire.
 *
 * Not ported from the design: the hero-scroll IntersectionObserver that
 * toggles the design kit's own Masthead search visibility/stickiness. The
 * real Header (customizations/components/theme/Header/Header.jsx) always
 * renders its SearchWidget and is not position:sticky — there is nothing
 * for that behaviour to toggle without also modifying the shared Header,
 * which is out of scope here (CONTRACT C masthead/nav port is a separate
 * task). Also not ported: the design's PageFrame wrapper (`.prc-kit`,
 * masthead/banner/main scaffolding) — `.prc-kit` is already applied to
 * `<body>` by SiteTheme.jsx, and the masthead/nav live in Header.jsx, so
 * this component only needs to emit the hero band + `.prc-kit__main`
 * content, matching what PageFrame would put *below* its own chrome.
 *
 * Data-shape assumptions carried over unchanged from the pre-port markup
 * (not re-verified here — see queries.js / asyncData.js for the actual
 * `searchContent` calls): each `added`/`updated` item exposes `docid`,
 * `Title`, `status` (string or `{title, token}` vocabulary summary),
 * `effective`/`superseded_date` (ISO date string), and `@id`. The design's sample
 * data also carries a `title.sub` relationship note (e.g. "Superseded by
 * DOE-STD-1020-2016") on updated rows — there is no equivalent field wired
 * into the real searchContent metadata today, so `prc-table__sub` is
 * supported by the inline table renderer (if a future data source ever
 * puts `{label, sub}` on an item's `Title`) but is never populated by the
 * current queries. Wiring a real "sub" source (e.g. a supersededBy
 * relation) is out of scope for this markup-only port.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import useSiteConfig from '../../helpers/useSiteConfig';
import SiteSEO from '../SEO/SiteSEO';
import { renderValue } from '../../helpers/renderValue';
import {
  buildHomeQueries,
  SUBREQUEST_NAMES,
  ARCHIVED_PRESET_ID,
  CURRENT_PRESET_ID,
  RECENT_ADDED_PRESET_ID,
  RECENT_DAYS,
} from './queries';

/** Nav labels this widget treats as "corpora" — filtered against the active
 * site's real nav (siteConfig/branding/*.ts) so each archive only shows the
 * content types it actually has (e.g. standards has no Glossary/Delegations,
 * nnsa has no People/Glossary). */
const CONTENT_CORPORA = [
  'Documents',
  'People',
  'Delegations',
  'Glossary',
  'News',
];

/** CONTRACT B columns for both home tables (design: HOME_COLUMNS), except the
 * date column's label — a bare "Date" says nothing about what it is, and the
 * underlying field actually flips meaning between the two tables (`issue_date`
 * for Recently added, `superseded_date` for Recently superseded & cancelled —
 * see `itemToRow`), so each caller supplies the label that matches its own
 * `dateField`. */
const homeColumns = (dateLabel) => [
  { key: 'docid', label: 'Doc #', kind: 'docid' },
  { key: 'title', label: 'Title', kind: 'title' },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'date', label: dateLabel, align: 'right' },
];

/** Map a raw searchContent item -> the flat row shape the table below
 * renders. `dateField` picks `issue_date` (added — the date DOE issued the
 * document, never our own `created` import stamp or Plone's `effective`
 * posting date) vs `superseded_date` (superseded & cancelled — the real DOE
 * retirement date, never our own `modified` bookkeeping timestamp). */
function itemToRow(item, dateField) {
  return {
    id: item['@id'] || item.UID,
    // `ext` rides along with `url` because the check belongs to the URL, not
    // to the cell: a hit from another archive keeps its absolute @id (that is
    // the point of cross-site search), and flattenToAppURL leaves it intact.
    // Same guard as TableListing.jsx.
    url: item['@id'] ? flattenToAppURL(item['@id']) : null,
    ext: item['@id'] ? /^https?:\/\//i.test(flattenToAppURL(item['@id'])) : false,
    docid: item.docid,
    title: item.Title,
    sub:
      item.Title && typeof item.Title === 'object' ? item.Title.sub : undefined,
    status: item.status,
    date: item[dateField],
  };
}

/** CONTRACT B `renderCell`, scoped to HOME_COLUMNS' four `kind`s. */
function renderHomeCell(column, row) {
  switch (column.kind) {
    case 'title':
      return (
        <>
          {!row.url ? (
            row.title
          ) : row.ext ? (
            <a
              className="prc-table__title"
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.title}
            </a>
          ) : (
            <Link className="prc-table__title" to={row.url}>
              {row.title}
            </Link>
          )}
          {row.sub && <span className="prc-table__sub">{row.sub}</span>}
        </>
      );
    case 'docid':
      return <span className="prc-table__docid">{row.docid}</span>;
    case 'status':
      return renderValue(row.status, 'status');
    default:
      return renderValue(row.date, 'date');
  }
}

/** CONTRACT B results table, emitted inline (no TableListing import) so the
 * home widget owns its own `.prc-table` DOM per PORT-SPEC.md. */
function HomeTable({ rows, columns, caption, emptyMessage }) {
  return (
    <div className="prc-table-wrap">
      <table className="prc-table">
        {caption && (
          <caption className="prc-visually-hidden">{caption}</caption>
        )}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} data-align={c.align || 'left'} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={row.id || ri}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  data-align={c.align || 'left'}
                  data-label={c.label}
                  data-title={c.kind === 'title' ? '' : undefined}
                >
                  {renderHomeCell(c, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <div className="prc-table__empty">{emptyMessage}</div>}
    </div>
  );
}

/** Large hero search input — CONTRACT-adjacent `.prc-search--lg` DOM (the
 * design's `SearchInput` component, variant="hero"). No scope selector: the
 * design's cross-archive scope dropdown is only ever shown disabled/noted
 * (no @querystring-search cross-archive index exists behind this addon
 * today — see design/ui_kits/home/HomeScreen.jsx CROSS_ARCHIVE_SEARCH), so
 * it was never a live option here. Same submit target as before the port
 * (`/search?SearchableText=…`, site-relative — this addon flattens the
 * site prefix out of every route, see siteConfig/branding/index.ts). */
function HeroSearch() {
  const history = useHistory();
  const [value, setValue] = React.useState('');

  const onSubmit = (event) => {
    event.preventDefault();
    const q = value.trim();
    history.push(
      q ? `/search?SearchableText=${encodeURIComponent(q)}` : '/search',
    );
  };

  return (
    <form
      className="prc-search prc-search--lg"
      role="search"
      onSubmit={onSubmit}
    >
      <label htmlFor="home-search-input" className="prc-visually-hidden">
        Search
      </label>
      <div className="prc-search__field">
        <div className="prc-search__inputwrap">
          <span className="prc-search__icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16" y1="16" x2="21" y2="21" />
            </svg>
          </span>
          <input
            id="home-search-input"
            type="search"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Search by document number, title, or keyword…"
            autoComplete="off"
          />
        </div>
      </div>
      <button type="submit" className="prc-search__btn">
        Search
      </button>
    </form>
  );
}

const SiteHome = ({ content }) => {
  const dispatch = useDispatch();
  const site = useSiteConfig();
  const subrequests = useSelector((state) => state.search.subrequests);

  // Client-side safety net — see file header. SSR already covers the normal
  // case via asyncData.js's homeAsyncPropExtender.
  useEffect(() => {
    const queries = buildHomeQueries(site.id);
    SUBREQUEST_NAMES.forEach((name) => {
      const key = `home-${name}`;
      const existing = subrequests[key];
      if (!existing || (!existing.loaded && !existing.loading)) {
        dispatch(searchContent('', queries[name], key));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);

  const added = subrequests['home-added'] || {};
  const updated = subrequests['home-updated'] || {};
  const counts = {
    current: subrequests['home-current']?.total ?? 0,
    total: subrequests['home-total']?.total ?? 0,
    recent: subrequests['home-recent']?.total ?? 0,
  };

  const corpora = (site.nav || []).filter((item) =>
    CONTENT_CORPORA.includes(item.label),
  );
  const archivedPresetId = ARCHIVED_PRESET_ID[site.id];
  const currentPresetId = CURRENT_PRESET_ID[site.id];

  const addedRows = (added.items || []).map((item) =>
    itemToRow(item, 'issue_date'),
  );
  const updatedRows = (updated.items || []).map((item) =>
    itemToRow(item, 'superseded_date'),
  );

  return (
    <>
      {/* Absolute canonical/og/JSON-LD for the site root, from
          SiteConfig.baseUrl rather than core's request-derived
          publicURL. Rendered here (below core's ContentMetadataTags)
          so helmet's canonical dedupe keeps THIS one. See
          components/SEO/SiteSEO.jsx. */}
      <SiteSEO content={content} />
      <section className="prc-kit__home-search">
        <div className="prc-kit__home-search-inner">
          {/* The h1 names the ARCHIVE, not the task. It used to read
              "Find a policy document" -- a search-widget label standing in the
              page's only h1, with the site's name demoted to the eyebrow above
              it. Measured 2026-09-01, the directives home ranked 38.5 for "doe
              directives" and the standards home 29.0 for "doe technical
              standards", both behind their own inner pages, taking 0 clicks
              across every named query. The instruction keeps its place as the
              eyebrow, where a short call to action belongs.

              The provenance line that used to sit under the tagline moved to
              the affiliation notice above the masthead (BL-0114), where it runs
              on every page rather than on this one only. `seoDescription` still
              carries the same disavowal into the SERP snippet, which is a
              separate surface and is unaffected. See SiteConfig.seoName. */}
          <p className="prc-kit__eyebrow">Find a policy document</p>
          <h1>{site.seoName}</h1>
          <p className="pp-home-tagline">{site.tagline}</p>

          <HeroSearch />
          <Link className="prc-kit__adv-link" to="/search">
            Advanced search
          </Link>

          {/* The first two stats deep-link to the browse page that produces
              exactly that number: `current` is the same preset the count is
              built from, `total` is the unfiltered family. The third has no
              honest target yet — the browse page reads only `?preset=<id>`
              (withPresetData/browseState), and a "last N days" preset cannot
              be expressed statically because `issue_date` has no
              largerThanRelativeDate operation registered. Linking it to the
              unfiltered list would show a different set than the number
              claims, so it stays plain text until that is built. */}
          <div className="prc-kit__stats">
            <Link
              className="prc-kit__stat"
              to={`/documents?preset=${currentPresetId}`}
            >
              <b>{counts.current}</b>
              <span>Current</span>
            </Link>
            <span className="prc-kit__dot" aria-hidden="true">
              &middot;
            </span>
            <Link className="prc-kit__stat" to="/documents">
              <b>{counts.total}</b>
              <span>Total documents</span>
            </Link>
            <span className="prc-kit__dot" aria-hidden="true">
              &middot;
            </span>
            <span className="prc-kit__stat">
              <b>{counts.recent}</b>
              <span>Issued in the last {RECENT_DAYS} days</span>
            </span>
          </div>

          {corpora.length > 0 && (
            <nav className="prc-kit__corpora" aria-label="Browse the corpora">
              {corpora.map((item, i) => (
                <React.Fragment key={item.to}>
                  {i > 0 && (
                    <span className="prc-kit__dot" aria-hidden="true">
                      &middot;
                    </span>
                  )}
                  <Link to={item.to}>{item.label}</Link>
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>
      </section>

      {/* div, not main: Volto's App already renders the page's
          <main>, so this nested a second main landmark inside it
          (TAIUIT item 15). Every other view uses a div with this
          same class; the homepage was the outlier. */}
      <div className="prc-kit__main">
        <div className="prc-kit__home-recent">
          <section className="prc-kit__home-sec">
            {/* Deep-links to the acquisition-ordered listing, not to the
                unfiltered /documents (which is still linked from the "Total
                documents" stat above). This table shows 3 rows ordered by
                issue_date, and a batch acquisition shares one issue_date --
                so without this link the rest of a batch has no inbound
                internal link at all. See the 'recently-added' preset. */}
            <h2>
              Recently added{' '}
              <Link to={`/documents?preset=${RECENT_ADDED_PRESET_ID}`}>
                All recent additions &rarr;
              </Link>
            </h2>
            <HomeTable
              rows={addedRows}
              columns={homeColumns('Issued')}
              caption="Recently added documents"
              emptyMessage="No documents found."
            />
          </section>
          <section className="prc-kit__home-sec">
            <h2>
              Recently superseded &amp; cancelled{' '}
              <Link to={`/documents?preset=${archivedPresetId}`}>
                All superseded &amp; cancelled &rarr;
              </Link>
            </h2>
            <HomeTable
              rows={updatedRows}
              columns={homeColumns('Superseded / Cancelled')}
              caption="Recently superseded and cancelled documents"
              emptyMessage="No documents found."
            />
          </section>
        </div>
      </div>
    </>
  );
};

export default SiteHome;
